import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../utils/authContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import Toast from "../../components/Toast/toast.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import { 
    fetchDishTagSorting, 
    updateDishTagSorting, 
    updateDishSorting,
    getAllMenus
} from "../../services/menu-service.js";
import { DISH_IMAGE_PLACEHOLDER_48 } from "../../utils/dishImagePlaceholder.js";

const Ordering = () => {
    const [loading, setLoading] = useState(true);
    const [tagData, setTagData] = useState([]);
    const [expandedTags, setExpandedTags] = useState({});
    const [savingTags, setSavingTags] = useState(false);
    const [savingDishes, setSavingDishes] = useState(false);
    const [activeTagId, setActiveTagId] = useState(null);
    const [draggedTag, setDraggedTag] = useState(null);
    const [draggedDish, setDraggedDish] = useState(null);
    const [draggedTagId, setDraggedTagId] = useState(null);
    const [dragOverTagIndex, setDragOverTagIndex] = useState(null);
    const [dragOverDishIndex, setDragOverDishIndex] = useState(null);

    // Track original orders and changes
    const [originalTagOrder, setOriginalTagOrder] = useState([]);
    const [originalDishOrders, setOriginalDishOrders] = useState({});
    const [tagOrderChanged, setTagOrderChanged] = useState(false);
    const [changedDishTags, setChangedDishTags] = useState(new Set());

    // Mobile-specific state
    const [isMobile, setIsMobile] = useState(false);
    const [touchStartY, setTouchStartY] = useState(0);
    const [touchStartX, setTouchStartX] = useState(0);
    const [isTouching, setIsTouching] = useState(false);
    const [longPressTimer, setLongPressTimer] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const { chefId } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const toastRef = useRef(null);

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Helper function to get translated value from translations array
    const getTranslatedValue = (translations, defaultValue) => {
        if (!translations || translations.length === 0) return defaultValue;
        // Treat 'hy' and 'am' as the same language
        const effectiveLanguage = language === 'am' ? 'hy' : language;
        const translation = translations.find(t => t.languageCode === effectiveLanguage);
        return translation?.value || defaultValue;
    };

    // Check if tag order has changed
    const checkTagOrderChanged = (currentTags) => {
        const currentOrder = currentTags.map(tag => tag.id);
        const hasChanged = JSON.stringify(currentOrder) !== JSON.stringify(originalTagOrder);
        setTagOrderChanged(hasChanged);
    };

    // Check if dish order has changed for a specific tag
    const checkDishOrderChanged = (tagId, currentDishes) => {
        const currentOrder = currentDishes.map(dish => dish.id);
        const originalOrder = originalDishOrders[tagId] || [];
        const hasChanged = JSON.stringify(currentOrder) !== JSON.stringify(originalOrder);
        
        setChangedDishTags(prev => {
            const newSet = new Set(prev);
            if (hasChanged) {
                newSet.add(tagId);
            } else {
                newSet.delete(tagId);
            }
            return newSet;
        });
    };

    // Move tag up or down (mobile-friendly)
    const moveTag = (fromIndex, direction) => {
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        if (toIndex < 0 || toIndex >= tagData.length) return;

        const newTags = [...tagData];
        const [movedTag] = newTags.splice(fromIndex, 1);
        newTags.splice(toIndex, 0, movedTag);
        
        setTagData(newTags);
        checkTagOrderChanged(newTags);
    };

    // Move dish up or down within a tag (mobile-friendly)
    const moveDish = (tagId, dishIndex, direction) => {
        const tagIndex = tagData.findIndex(tag => tag.id === tagId);
        if (tagIndex === -1) return;

        const toIndex = direction === 'up' ? dishIndex - 1 : dishIndex + 1;
        if (toIndex < 0 || toIndex >= tagData[tagIndex].dishes.length) return;

        const newTagData = [...tagData];
        const dishes = [...newTagData[tagIndex].dishes];
        const [movedDish] = dishes.splice(dishIndex, 1);
        dishes.splice(toIndex, 0, movedDish);

        newTagData[tagIndex] = {
            ...newTagData[tagIndex],
            dishes
        };

        setTagData(newTagData);
        checkDishOrderChanged(tagId, dishes);
    };

    // Ref to prevent duplicate API calls
    const isFetchingData = useRef(false);

    // Fetch tag and dish data
    const fetchData = useCallback(async () => {
        if (isFetchingData.current) return;
        isFetchingData.current = true;
        setLoading(true);
        try {
            const data = await fetchDishTagSorting(chefId);
            if (data) {
                // Sort dishes within each tag by priorityIndex
                const sortedData = data.map(tag => ({
                    ...tag,
                    dishes: tag.dishes ? tag.dishes.sort((a, b) => {
                        // Handle cases where priorityIndex might be null or undefined
                        const priorityA = a.priorityIndex ?? Number.MAX_SAFE_INTEGER;
                        const priorityB = b.priorityIndex ?? Number.MAX_SAFE_INTEGER;
                        return priorityA - priorityB;
                    }) : []
                }));
                
                setTagData(sortedData);
                
                // Store original orders (after sorting by priorityIndex)
                const tagOrder = sortedData.map(tag => tag.id);
                setOriginalTagOrder(tagOrder);
                
                const dishOrders = {};
                sortedData.forEach(tag => {
                    dishOrders[tag.id] = tag.dishes.map(dish => dish.id);
                });
                setOriginalDishOrders(dishOrders);
                
                // Reset change states
                setTagOrderChanged(false);
                setChangedDishTags(new Set());
                
                // Initialize expanded state for all tags
                const expandedState = {};
                sortedData.forEach(tag => {
                    expandedState[tag.id] = false;
                });
                setExpandedTags(expandedState);
            }
        } catch (error) {
            console.error("Error fetching ordering data:", error);
        } finally {
            setLoading(false);
            isFetchingData.current = false;
        }
    }, [chefId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Toggle expanded state for a tag
    const toggleTagExpand = (tagId) => {
        if (isDragging) return; // Prevent expanding during drag
        setExpandedTags(prev => ({
            ...prev,
            [tagId]: !prev[tagId]
        }));
    };

    // Enhanced touch handlers for mobile
    const handleTouchStart = (e, type, index, tagId = null) => {
        const touch = e.touches[0];
        setTouchStartY(touch.clientY);
        setTouchStartX(touch.clientX);
        setIsTouching(true);
        setIsDragging(false);

        // Long press for drag on mobile
        const timer = setTimeout(() => {
            setIsDragging(true);
            if (type === 'tag') {
                setDraggedTag(index);
            } else {
                setDraggedDish(index);
                setDraggedTagId(tagId);
            }
            
            // Haptic feedback if available
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        }, 200); // 200ms long press

        setLongPressTimer(timer);
    };

    const handleTouchMove = (e, type, index, tagId = null) => {
        if (!isTouching) return;

        const touch = e.touches[0];
        const deltaY = touch.clientY - touchStartY;
        const deltaX = touch.clientX - touchStartX;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // If moved too much before long press, cancel
        if (distance > 15 && !isDragging) {
            if (longPressTimer) {
                clearTimeout(longPressTimer);
                setLongPressTimer(null);
            }
            setIsTouching(false);
            return;
        }

        if (isDragging) {
            e.preventDefault();
            
            // Find the element under the touch point
            const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
            if (elementBelow) {
                const targetIndex = parseInt(elementBelow.dataset.index);
                const targetTagId = elementBelow.dataset.tagId;

                if (!isNaN(targetIndex)) {
                    if (type === 'tag') {
                        setDragOverTagIndex(targetIndex);
                    } else if (targetTagId === tagId) {
                        setDragOverDishIndex(targetIndex);
                    }
                }
            }
        }
    };

    const handleTouchEnd = (type, tagId = null) => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }

        if (isDragging) {
            if (type === 'tag') {
                handleTagDrop();
            } else {
                handleDishDrop(tagId);
            }
        }

        setIsTouching(false);
        setIsDragging(false);
        setDraggedTag(null);
        setDraggedDish(null);
        setDraggedTagId(null);
        setDragOverTagIndex(null);
        setDragOverDishIndex(null);
    };

    // Original drag handlers for desktop
    const handleTagDragStart = (tagIndex) => {
        setDraggedTag(tagIndex);
    };

    const handleTagDragEnd = () => {
        setDraggedTag(null);
        setDragOverTagIndex(null);
    };

    const handleTagDragOver = (tagIndex) => {
        if (draggedTag !== null && draggedTag !== tagIndex) {
            setDragOverTagIndex(tagIndex);
        }
    };

    const handleTagDrop = () => {
        if (draggedTag !== null && dragOverTagIndex !== null && draggedTag !== dragOverTagIndex) {
            const newTags = [...tagData];
            const draggedItem = newTags[draggedTag];

            newTags.splice(draggedTag, 1);
            newTags.splice(dragOverTagIndex, 0, draggedItem);

            setTagData(newTags);
            checkTagOrderChanged(newTags);
        }

        setDraggedTag(null);
        setDragOverTagIndex(null);
    };

    const handleDishDragStart = (tagId, dishIndex) => {
        setDraggedDish(dishIndex);
        setDraggedTagId(tagId);
    };

    const handleDishDragEnd = () => {
        setDraggedDish(null);
        setDraggedTagId(null);
        setDragOverDishIndex(null);
    };

    const handleDishDragOver = (dishIndex) => {
        if (draggedDish !== null && draggedDish !== dishIndex) {
            setDragOverDishIndex(dishIndex);
        }
    };

    const handleDishDrop = (tagId) => {
        if (draggedDish !== null && dragOverDishIndex !== null && draggedTagId === tagId && draggedDish !== dragOverDishIndex) {
            const tagIndex = tagData.findIndex(tag => tag.id === tagId);

            if (tagIndex !== -1) {
                const newTagData = [...tagData];
                const dishes = [...newTagData[tagIndex].dishes];
                const draggedItem = dishes[draggedDish];

                dishes.splice(draggedDish, 1);
                dishes.splice(dragOverDishIndex, 0, draggedItem);

                newTagData[tagIndex] = {
                    ...newTagData[tagIndex],
                    dishes
                };

                setTagData(newTagData);
                checkDishOrderChanged(tagId, dishes);
            }
        }

        setDraggedDish(null);
        setDraggedTagId(null);
        setDragOverDishIndex(null);
    };

    // Save tag order
    const saveTagOrder = async () => {
        setSavingTags(true);
        try {
            const orderedTagIds = tagData.map(tag => tag.id);
            await updateDishTagSorting(chefId, orderedTagIds);
            
            // Update original order and reset change state
            setOriginalTagOrder(orderedTagIds);
            setTagOrderChanged(false);
            
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.statusUpdated', language) || "Order saved successfully",
                    "success"
                );
            }
        } catch (error) {
            console.error("Error saving tag order:", error);
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.updateFailed', language) || "Failed to save order",
                    "error"
                );
            }
        } finally {
            setSavingTags(false);
        }
    };

    // Save dish order for a specific tag
    const saveDishOrder = async (tagId) => {
        setActiveTagId(tagId);
        setSavingDishes(true);
        try {
            const tag = tagData.find(t => t.id === tagId);
            if (!tag) {
                if (toastRef.current) {
                    toastRef.current.addToast(
                        getTranslation('common.error', language) || "Tag not found",
                        "error"
                    );
                }
                return;
            }

            // CRITICAL: API requires ALL dishes for this tag, not just visible ones
            // Fetch ALL dishes for this chef to ensure we have complete list
            const allDishesResponse = await getAllMenus(chefId, 0, 10000); // Fetch large number to get all
            const allDishes = allDishesResponse?.dishDtoTreeSet || [];
            
            // Filter to get ALL dishes for this specific tag
            const allDishesForTag = allDishes.filter(dish => 
                dish.dishTagDto && dish.dishTagDto.id === tagId
            );
            
            // Get the current order from UI (tag.dishes)
            const currentDishIds = tag.dishes.map(dish => dish.id);
            
            // Create a map of current dish IDs to their order
            const currentOrderMap = new Map();
            currentDishIds.forEach((id, index) => {
                currentOrderMap.set(id, index);
            });
            
            // Get all dish IDs for this tag
            const allDishIdsForTag = allDishesForTag.map(dish => dish.id);
            
            // Check if we're missing any dishes in the current order
            const missingDishIds = allDishIdsForTag.filter(id => !currentOrderMap.has(id));
            
            // Build the complete ordered array:
            // 1. Start with dishes in current UI order
            // 2. Append any missing dishes (dishes that exist in backend but not in UI)
            const orderedDishIds = [...currentDishIds];
            if (missingDishIds.length > 0) {
                // Add missing dishes at the end
                orderedDishIds.push(...missingDishIds);
            }
            
            // Validate we have the correct number of dishes
            if (orderedDishIds.length !== allDishIdsForTag.length) {
                console.error(`Dish count mismatch: UI has ${currentDishIds.length}, backend has ${allDishIdsForTag.length}, ordered array has ${orderedDishIds.length}`);
                if (toastRef.current) {
                    toastRef.current.addToast(
                        getTranslation('common.error', language) || "Dish count mismatch. Please refresh and try again",
                        "error"
                    );
                }
                return;
            }
            
            // Validate we have dishes
            if (orderedDishIds.length === 0) {
                if (toastRef.current) {
                    toastRef.current.addToast(
                        getTranslation('common.error', language) || "No dishes found for this tag",
                        "error"
                    );
                }
                return;
            }
            
            // Call API - only show success if this succeeds
            const success = await updateDishSorting(chefId, tagId, orderedDishIds);
            
            if (!success) {
                // updateDishSorting returns false on error
                if (toastRef.current) {
                    toastRef.current.addToast(
                        getTranslation('toast.updateFailed', language) || "Failed to save order",
                        "error"
                    );
                }
                return;
            }
            
            // Only update state and show success if API call succeeded
            setOriginalDishOrders(prev => ({
                ...prev,
                [tagId]: orderedDishIds
            }));
            setChangedDishTags(prev => {
                const newSet = new Set(prev);
                newSet.delete(tagId);
                return newSet;
            });
            
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.statusUpdated', language) || "Order saved successfully",
                    "success"
                );
            }
        } catch (error) {
            console.error("Error saving dish order:", error);
            if (toastRef.current) {
                toastRef.current.addToast(
                    getTranslation('toast.updateFailed', language) || "Failed to save order",
                    "error"
                );
            }
        } finally {
            setSavingDishes(false);
            setActiveTagId(null);
        }
    };

    // Handle back to menu
    const handleBackToMenu = () => {
        navigate("/menu");
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
        <div className="transition-all duration-300">
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">{getTranslation('menu.orderingPageTitle', language)}</h1>
            
            {/* Header Section */}
            <div className="flex items-center justify-between mb-6">
                <Button
                    variant="secondary"
                    size="md"
                    onClick={handleBackToMenu}
                    className="whitespace-nowrap font-bold"
                >
                    {getTranslation('menuPage.backToList', language)}
                </Button>
            </div>

            {/* Save Tag Order Button */}
            {tagOrderChanged && (
                <div className="mb-6">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={saveTagOrder}
                        disabled={savingTags}
                        loading={savingTags}
                    >
                        {getTranslation('menu.saveTagsOrder', language)}
                    </Button>
                </div>
            )}

            {/* Tags List */}
            <div className="space-y-4">
                {tagData.map((tag, index) => (
                    <motion.div
                        key={tag.id}
                        data-index={index}
                        className={`bg-white dark:bg-[#161b22] rounded-lg shadow-md overflow-hidden transition-all duration-300 border dark:border-[#30363d] ${
                            dragOverTagIndex === index ? 'border-2 border-gray-400 dark:border-gray-500' : ''
                        } ${isDragging && draggedTag === index ? 'opacity-60' : ''}`}
                        draggable={!isMobile}
                        onDragStart={() => !isMobile && handleTagDragStart(index)}
                        onDragEnd={() => !isMobile && handleTagDragEnd()}
                        onDragOver={(e) => {
                            if (!isMobile) {
                                e.preventDefault();
                                handleTagDragOver(index);
                            }
                        }}
                        onDrop={(e) => {
                            if (!isMobile) {
                                e.preventDefault();
                                handleTagDrop();
                            }
                        }}
                        onTouchStart={(e) => isMobile && handleTouchStart(e, 'tag', index)}
                        onTouchMove={(e) => isMobile && handleTouchMove(e, 'tag', index)}
                        onTouchEnd={() => isMobile && handleTouchEnd('tag')}
                    >
                        {/* Tag Header */}
                        <div className="bg-white dark:bg-[#0d1117] p-4 border-b border-gray-200 dark:border-[#30363d]">
                            <div className="flex items-center justify-between">
                                <div 
                                    className="flex items-center flex-1 cursor-pointer"
                                    onClick={() => toggleTagExpand(tag.id)}
                                >
                                    <div className={`mr-3 text-gray-400 dark:text-[#8b949e] ${!isMobile ? 'cursor-grab' : ''}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900 dark:text-[#c9d1d9]">
                                            {getTranslatedValue(tag.translations, tag.tag)}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-[#8b949e] mt-1 font-medium">
                                            {tag.dishes.length} {tag.dishes.length === 1 ? 
                                                (getTranslation('menu.dish', language) || 'dish') : 
                                                (getTranslation('menu.dishes', language) || 'dishes')
                                            }
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Mobile Controls */}
                                {isMobile && (
                                    <div className="flex items-center mr-3">
                                        <button
                                            className="p-2 text-gray-600 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveTag(index, 'up');
                                            }}
                                            disabled={index === 0}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                                            </svg>
                                        </button>
                                        <button
                                            className="p-2 text-gray-600 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                moveTag(index, 'down');
                                            }}
                                            disabled={index === tagData.length - 1}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                
                                {/* Expand/Collapse Button */}
                                <button
                                    className="p-2 hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded transition-all duration-200"
                                    onClick={() => toggleTagExpand(tag.id)}
                                >
                                    <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        className={`h-5 w-5 text-gray-600 dark:text-[#8b949e] transition-transform duration-200 ${expandedTags[tag.id] ? 'transform rotate-180' : ''}`} 
                                        fill="none" 
                                        viewBox="0 0 24 24" 
                                        stroke="currentColor"
                                        strokeWidth={2.5}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Tag Content - Dishes */}
                        <AnimatePresence>
                            {expandedTags[tag.id] && (
                                <motion.div 
                                    className="p-4"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Save Dishes Button */}
                                    {changedDishTags.has(tag.id) && (
                                        <div className="mb-4 flex justify-end">
                                            <button
                                                className="text-white text-sm bg-customBackground hover:bg-customHoverBlack px-4 py-2 rounded-md transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => saveDishOrder(tag.id)}
                                                disabled={savingDishes && activeTagId === tag.id}
                                            >
                                                {savingDishes && activeTagId === tag.id ? (
                                                    <span className="flex items-center">
                                                        <div className="mr-2">
                                                            <LoadingSpinner size="small" />
                                                        </div>
                                                        {getTranslation('common.saving', language)}
                                                    </span>
                                                ) : (
                                                    getTranslation('menu.saveDishesOrder', language)
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {/* Dishes List */}
                                    <div className="space-y-3">
                                        {tag.dishes.map((dish, dishIndex) => (
                                            <motion.div
                                                key={dish.id}
                                                data-index={dishIndex}
                                                data-tag-id={tag.id}
                                                className={`bg-white dark:bg-[#0d1117] rounded-md p-3 flex items-center transition-all duration-300 border dark:border-[#30363d] ${
                                                    dragOverDishIndex === dishIndex && draggedTagId === tag.id ? 'border-2 border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-900/30' : 'hover:bg-gray-50 dark:hover:bg-[#161b22]'
                                                } ${isDragging && draggedDish === dishIndex && draggedTagId === tag.id ? 'opacity-60' : ''}`}
                                                draggable={!isMobile}
                                                onDragStart={() => !isMobile && handleDishDragStart(tag.id, dishIndex)}
                                                onDragEnd={() => !isMobile && handleDishDragEnd()}
                                                onDragOver={(e) => {
                                                    if (!isMobile) {
                                                        e.preventDefault();
                                                        handleDishDragOver(dishIndex);
                                                    }
                                                }}
                                                onDrop={(e) => {
                                                    if (!isMobile) {
                                                        e.preventDefault();
                                                        handleDishDrop(tag.id);
                                                    }
                                                }}
                                                onTouchStart={(e) => isMobile && handleTouchStart(e, 'dish', dishIndex, tag.id)}
                                                onTouchMove={(e) => isMobile && handleTouchMove(e, 'dish', dishIndex, tag.id)}
                                                onTouchEnd={() => isMobile && handleTouchEnd('dish', tag.id)}
                                            >
                                                {/* Mobile Controls */}
                                                {isMobile && (
                                                    <div className="flex flex-col mr-3">
                                                        <button
                                                            className="p-1 text-gray-600 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                moveDish(tag.id, dishIndex, 'up');
                                                            }}
                                                            disabled={dishIndex === 0}
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="p-1 text-gray-600 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                moveDish(tag.id, dishIndex, 'down');
                                                            }}
                                                            disabled={dishIndex === tag.dishes.length - 1}
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {/* Dish Image */}
                                                <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-gray-200 dark:bg-[#161b22] mr-3 border dark:border-[#30363d]">
                                                    <img 
                                                        key={dish.url} // Use URL as key to help browser cache
                                                        src={dish.url} 
                                                        alt={dish.nameHy} 
                                                        className="w-full h-full object-cover transition-opacity duration-300"
                                                        onLoad={(e) => {
                                                            e.target.style.opacity = "1";
                                                        }}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = DISH_IMAGE_PLACEHOLDER_48;
                                                            e.target.style.opacity = "1";
                                                        }}
                                                        loading="lazy"
                                                        decoding="async"
                                                        fetchpriority="low"
                                                        style={{ contentVisibility: 'auto', opacity: 0 }}
                                                    />
                                                </div>
                                                
                                                {/* Dish Info */}
                                                <div className="flex-1">
                                                    <h4 className="font-bold text-gray-900 dark:text-[#c9d1d9]">
                                                        {dish[`name${language.charAt(0).toUpperCase() + language.slice(1)}`] || dish.nameHy}
                                                    </h4>
                                                    <p className="text-sm text-gray-500 dark:text-[#8b949e] font-medium">{dish.price} AMD</p>
                                                </div>
                                                
                                                {/* Desktop Drag Handle */}
                                                {!isMobile && (
                                                    <div className="ml-3 text-gray-400 dark:text-[#8b949e] hover:text-gray-600 dark:hover:text-[#b1bac4] cursor-grab transition-all duration-200">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))}
            </div>

            {/* Empty State */}
            {tagData.length === 0 && (
                <div className="text-center py-12">
                    <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-md p-8 max-w-md mx-auto border dark:border-[#30363d]">
                        <svg className="w-16 h-16 text-gray-400 dark:text-[#8b949e] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-[#c9d1d9] mb-2">
                            {getTranslation('menu.noTags', language) || 'No menu categories found'}
                        </h3>
                        <p className="text-gray-600 dark:text-[#b1bac4] font-medium">
                            {getTranslation('menu.noTagsDescription', language) || 'Create some menu categories first to organize your dishes.'}
                        </p>
                    </div>
                </div>
            )}
        </div>
        <Toast ref={toastRef} />
        </>
    );
};

export default Ordering;
